import React, { useState, useContext, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import CustomPanel from "./Panel";
import { cn } from "utils";
import { FilterPanelContext } from "context/providers/FilterPanelProvider";

const CollapsibleCustomPanel = ({
  title,
  sectionKey,
  defaultCollapsed = true,
  children,
  className,
  ...customPanelProps
}) => {
  const storageKey = useMemo(
    () => `filter-section-${sectionKey}`,
    [sectionKey]
  );

  const getInitialCollapsedState = () => {
    const storedState = sessionStorage.getItem(storageKey);
    if (storedState !== null) {
      return storedState === "true";
    }
    return defaultCollapsed;
  };

  const [isCollapsed, setIsCollapsed] = useState(getInitialCollapsedState());
  const context = useContext(FilterPanelContext);

  useEffect(() => {
    if (!context) return;

    const updateCallback = (collapsed) => {
      setIsCollapsed(collapsed);
      sessionStorage.setItem(storageKey, collapsed.toString());
    };

    context.registerPanel(sectionKey, updateCallback);
    return () => context.unregisterPanel(sectionKey);
  }, [sectionKey, context, storageKey]);

  const toggleCollapse = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    sessionStorage.setItem(storageKey, newState.toString());
  };

  return (
    <div className={cn("font-rubik", className)} data-collapsed={isCollapsed}>
      <CustomPanel
        title={
          <div
            className="flex w-full cursor-pointer select-none items-center"
            onClick={toggleCollapse}
          >
            <svg
              className={cn(
                "mr-3 h-3 w-3 shrink-0 fill-gray-400 transition-transform",
                {
                  "fill-gray-600": !isCollapsed,
                  "-rotate-90": isCollapsed,
                }
              )}
              viewBox="0 0 9 5"
            >
              <path
                d="M4.5 5.55L0 1.05L1.05 0L4.5 3.45L7.95 0L9 1.05L4.5 5.55Z"
                fill="currentColor"
              />
            </svg>
            <span className="min-w-0 normal-case">{title}</span>
          </div>
        }
        isCollapsed={isCollapsed}
        {...customPanelProps}
      >
        <div
          className={cn({
            "h-0 overflow-hidden": isCollapsed,
            "h-auto": !isCollapsed,
          })}
        >
          {children}
        </div>
      </CustomPanel>
    </div>
  );
};

CollapsibleCustomPanel.propTypes = {
  title: PropTypes.string.isRequired,
  sectionKey: PropTypes.string.isRequired,
  defaultCollapsed: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
};

export default CollapsibleCustomPanel;
